import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';

const PLANS = {
  trial: {
    label: 'Free Trial',
    color: '#6c58f0',
    bg: '#1a1535',
    border: '#6c58f0',
  },
  starter: {
    label: 'Starter',
    color: '#3b82f6',
    bg: '#0f1e35',
    border: '#3b82f6',
  },
  growth: {
    label: 'Growth',
    color: '#10b981',
    bg: '#0a2018',
    border: '#10b981',
  },
  agency: {
    label: 'Agency',
    color: '#f59e0b',
    bg: '#221a05',
    border: '#f59e0b',
  },
};

export default function Billing() {
  const { tenant } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/billing')
      .then(res => setBilling(res.data))
      .catch(() => setError('Could not load billing information.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
      Loading billing info…
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>
  );

  const plan = billing?.plan || 'trial';
  const planMeta = PLANS[plan] || PLANS.trial;
  const trialEnd = billing?.trial_ends_at ? new Date(billing.trial_ends_at) : null;
  const subEnd = billing?.subscription_ends_at ? new Date(billing.subscription_ends_at) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / 86400000)) : null;
  const isActive = billing?.is_active;
  const limits = billing?.limits || {};

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 0' }}>

      {/* Plan card */}
      <div style={{
        background: planMeta.bg,
        border: `1px solid ${planMeta.border}`,
        borderRadius: 12,
        padding: '28px 32px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: planMeta.color, marginBottom: 6 }}>
            Current Plan
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            {planMeta.label}
          </div>
          {plan === 'trial' && trialEnd && (
            <div style={{ fontSize: '0.85rem', color: daysLeft <= 3 ? '#f59e0b' : 'var(--text2)' }}>
              {daysLeft > 0
                ? `Trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — ${trialEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : 'Trial has ended'}
            </div>
          )}
          {plan !== 'trial' && subEnd && (
            <div style={{ fontSize: '0.85rem', color: 'var(--text2)' }}>
              {isActive
                ? `Active until ${subEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : 'Subscription expired'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            padding: '4px 14px',
            borderRadius: 20,
            fontSize: '0.75rem',
            fontWeight: 700,
            background: isActive ? '#0d2e1a' : '#2d1515',
            color: isActive ? '#10b981' : '#ef4444',
            border: `1px solid ${isActive ? '#10b981' : '#ef4444'}`,
          }}>
            {isActive ? '● Active' : '● Inactive'}
          </span>
        </div>
      </div>

      {/* Usage limits */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 14 }}>
          Plan Limits
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          {[
            { label: 'Clients', value: limits.max_clients, icon: '⊙' },
            { label: 'Team Members', value: limits.max_team_members, icon: '⊕' },
            { label: 'Work Orders / Month', value: limits.max_work_orders_per_month, icon: '≡' },
          ].map(item => (
            <div key={item.label} style={{
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '16px 20px',
            }}>
              <div style={{ fontSize: '1.2rem', marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                {item.value ?? '—'}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade section */}
      <div style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '24px 28px',
      }}>
        <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          Need more capacity?
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
          Contact us to upgrade your plan and unlock more clients, team members, and work orders.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a
            href="mailto:subash@onehundredtech.com?subject=Leadex Plan Upgrade"
            style={{
              padding: '10px 22px',
              background: 'linear-gradient(135deg, #6c58f0, #4f8ef7)',
              color: '#fff',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.85rem',
              textDecoration: 'none',
              display: 'inline-block',
            }}>
            Contact for Upgrade
          </a>
          <a
            href="https://wa.me/919895000000"
            target="_blank"
            rel="noreferrer"
            style={{
              padding: '10px 22px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.85rem',
              textDecoration: 'none',
              display: 'inline-block',
            }}>
            WhatsApp Us
          </a>
        </div>
      </div>

      {/* Account info */}
      <div style={{ marginTop: 20, padding: '16px 20px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10 }}>
          Account
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div><span style={{ color: 'var(--text3)', marginRight: 8 }}>Agency:</span>{tenant?.name}</div>
          <div><span style={{ color: 'var(--text3)', marginRight: 8 }}>Plan ID:</span>{plan}</div>
          {billing?.stripe_customer_id && (
            <div><span style={{ color: 'var(--text3)', marginRight: 8 }}>Billing:</span>Stripe connected</div>
          )}
        </div>
      </div>

    </div>
  );
}
